/*
  Colour selection block for the main-product section.

  Fetches the complementary products of the current product through the Product
  Recommendations API — the same list the 'complementary' block uses — and
  appends one swatch per product next to the server-rendered current swatch.

  A dedicated element name (not Dawn's <product-recommendations>) is required:
  product-recommendations.js always reads the *first* <product-recommendations>
  node of a fetched section, so two of them in main-product would overwrite
  each other.
*/
if (!customElements.get('tf-color-variants')) {
  // Shared across instances so the same request is never made twice.
  const requests = new Map();

  customElements.define(
    'tf-color-variants',
    class TfColorVariants extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('.tf-color-variants__list');
        if (!this.list || !this.dataset.url) return;
        // Dawn re-renders product-info on variant change; never load twice.
        if (this.classList.contains('tf-color-variants--loaded')) return;

        this.observer = new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.unobserve(this);
            this.loadSwatches();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      get requestUrl() {
        const separator = this.dataset.url.includes('?') ? '&' : '?';
        return (
          this.dataset.url +
          separator +
          'product_id=' +
          encodeURIComponent(this.dataset.productId) +
          '&section_id=' +
          encodeURIComponent(this.dataset.sectionId)
        );
      }

      loadSwatches() {
        const url = this.requestUrl;

        if (!requests.has(url)) {
          requests.set(
            url,
            fetch(url).then((response) => {
              if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
              return response.text();
            })
          );
        }

        requests
          .get(url)
          .then((text) => this.render(text))
          .catch((error) => {
            console.error('tf-color-variants:', error);
            this.finish();
          });
      }

      render(text) {
        const fetched = new DOMParser().parseFromString(text, 'text/html');
        const items = fetched.querySelectorAll('.tf-color-variants__item');
        // The API already excludes the current product, but a merchant can list
        // it as its own complementary product — never show it twice.
        const seen = new Set(
          Array.from(this.list.children).map((item) => item.dataset.productId)
        );

        items.forEach((item) => {
          const productId = item.dataset.productId;
          if (!productId || seen.has(productId)) return;

          seen.add(productId);
          const swatch = document.importNode(item, true);
          swatch.classList.add('tf-color-variants__item--added');
          this.list.appendChild(swatch);
        });

        this.sortSwatches();
        this.finish();
      }

      sortSwatches() {
        const items = Array.from(this.list.children);

        if (this.dataset.sort === 'alphabetical') {
          items.sort((a, b) =>
            (a.dataset.handle || '').localeCompare(b.dataset.handle || '', undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          );
        } else if (this.dataset.currentPosition === 'first' || this.dataset.currentPosition === 'last') {
          const atStart = this.dataset.currentPosition === 'first';
          items.sort((a, b) => {
            const weight = (item) => (item.dataset.current === 'true' ? (atStart ? -1 : 1) : 0);
            return weight(a) - weight(b);
          });
        }

        items.forEach((item) => this.list.appendChild(item));
      }

      finish() {
        this.classList.add('tf-color-variants--loaded');

        // Read the state off the DOM rather than off this pass's tally, so a
        // repeat render can never hide a list that already has swatches.
        const siblings = this.list.querySelectorAll(
          '.tf-color-variants__item:not([data-current="true"])'
        ).length;

        this.classList.toggle('tf-color-variants--empty', siblings === 0);
        this.hidden =
          siblings === 0 && this.dataset.hideWhenEmpty === 'true' && !window.Shopify?.designMode;
      }
    }
  );
}
